import {useCountries} from 'api/countries';
import {useGetExportFile} from 'api/fileExport';
import {
    useConnectPaymentRequestSender,
    useGetPaymentRequestPublic,
} from 'api/payment-requests';
import {useAuth} from 'context/auth-context';
import {useBusinessPartner} from 'context/business-partner-context';
import {useLanguage} from 'context/language-context';
import {createPaymentRequesPayUrl} from 'helpers/url';
import {useState} from 'react';
import {useHistory, useParams} from 'react-router';
import {HashParamType} from 'types/common';

export type PaymentRequestPayMethod = 'peer' | 'iban';

const usePaymentRequestPublicPage = () => {
    const [error, setError] = useState<string>('');
    const history = useHistory();
    const [payMethod, setPayMethod] = useState<PaymentRequestPayMethod>();
    const {hash} = useParams<HashParamType>();
    const {user} = useAuth();
    const {activeBusinessPartner} = useBusinessPartner();
    const {data, isLoading} = useGetPaymentRequestPublic(
        hash,
        activeBusinessPartner,
    );
    const [loadingDocuments, setLoadingDocuments] = useState<string[]>([]);
    const {mutate: getDocument, isPending: isDownloadLoading} =
        useGetExportFile();
    const {mutate: connectPeer, isPending: isConnectPeerLoading} =
        useConnectPaymentRequestSender();
    const {selectedLanguageParams} = useLanguage();
    const {data: countries, isLoading: isCountriesLoading} = useCountries();

    const downloadDocument = (documentId: string) => {
        if (hash) {
            setLoadingDocuments(prev => [...prev, documentId]);
            getDocument(
                {
                    url: `/web_api/payment_requests_public/${hash}/download/${documentId
                        .split('/')
                        .pop()}`,
                },
                {
                    onSettled: () =>
                        setLoadingDocuments(prev =>
                            prev.filter(docId => docId !== documentId),
                        ),
                },
            );
        }
    };

    const handlePayMethodChange = (method: PaymentRequestPayMethod) => {
        setPayMethod(method);
    };

    const handleConnectPeer = () => {
        if (!data) {
            throw new Error(
                'Missing payment request data: ' + JSON.stringify(data),
            );
        }
        connectPeer(
            {requestId: data['@id'], activeBusinessPartner},
            {
                onError: (errorResponse: any) =>
                    setError(
                        errorResponse['hydra:description'] ||
                            'Something went wrong',
                    ),
            },
        );
    };

    const handleCreatePeerPayment = () => {
        if (!data) {
            throw new Error(
                'Missing payment request data: ' + JSON.stringify(data),
            );
        }
        history.push(
            createPaymentRequesPayUrl({
                paymentRequestHash: data.hash,
                contactId: data.businessPartner['@id'],
            }),
        );
    };

    const handleDissmissNotFound = () => {
        history.push('/');
    };

    return {
        data,
        isLoading: isLoading || isCountriesLoading,
        user,
        downloadDocument,
        isDownloadLoading,
        handlePayMethodChange,
        payMethod,
        activeBusinessPartner,
        handleConnectPeer,
        isConnectPeerLoading,
        handleCreatePeerPayment,
        error,
        handleDissmissNotFound,
        history,
        loadingDocuments,
        selectedLanguageParams,
        countries,
    };
};

export default usePaymentRequestPublicPage;
